import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Mail, Send } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContexto.js';
import { Botao } from '../componentes/ui/Botao.js';
import { SecaoBackupExterno } from './SecaoBackupExterno.js';

type Seguranca = 'NENHUMA' | 'STARTTLS' | 'TLS';

interface ConfigSmtp {
  /** Falso quando nunca foi salva. A API sempre devolve os campos, com padrões. */
  configurado: boolean;
  host: string;
  porta: number;
  seguranca: Seguranca;
  usuario: string | null;
  temSenha: boolean;
  remetenteNome: string;
  remetenteEmail: string;
  ativo: boolean;
  ultimoTesteEm: string | null;
  ultimoTesteOk: boolean | null;
  ultimoTesteErro: string | null;
}

/**
 * Marcador combinado com a API: manter a senha que já está gravada.
 *
 * Existe para a senha do servidor de e-mail nunca voltar ao navegador. Sem ele,
 * a tela precisaria receber a senha atual para não apagá-la ao salvar outro
 * campo — e aí bastaria um XSS na página para levar a credencial embora.
 */
const SENHA_INALTERADA = '__inalterada__';

const PORTA_PADRAO: Record<Seguranca, number> = {
  NENHUMA: 25,
  STARTTLS: 587,
  TLS: 465,
};

const vazio = {
  host: '',
  porta: 587,
  seguranca: 'STARTTLS' as Seguranca,
  usuario: '',
  senha: '',
  remetenteNome: 'rDNS Manager',
  remetenteEmail: '',
  ativo: false,
};

export function Configuracoes() {
  const qc = useQueryClient();
  const { usuario } = useSessao();

  const { data, isLoading } = useQuery({
    queryKey: ['configuracoes', 'smtp'],
    queryFn: () => api.get<ConfigSmtp | null>('/configuracoes/smtp'),
  });

  const [form, setForm] = useState(vazio);
  const [senhaTocada, setSenhaTocada] = useState(false);
  const [destino, setDestino] = useState('');

  useEffect(() => {
    if (!data) return;
    // Cada campo com padrão: um corpo incompleto — resposta vazia virando `{}`,
    // versão antiga da API atrás de um cache — preencheria o formulário com
    // `undefined` e o primeiro `.trim()` derrubaria a tela inteira. Foi o que
    // aconteceu na primeira versão desta página.
    setForm({
      host: data.host ?? '',
      porta: data.porta ?? 587,
      seguranca: data.seguranca ?? 'STARTTLS',
      usuario: data.usuario ?? '',
      senha: '',
      remetenteNome: data.remetenteNome ?? 'rDNS Manager',
      remetenteEmail: data.remetenteEmail ?? '',
      ativo: data.ativo ?? false,
    });
    setSenhaTocada(false);
  }, [data]);

  useEffect(() => {
    if (data?.remetenteEmail && !destino) setDestino(usuario?.email ?? '');
  }, [data, usuario, destino]);

  const salvar = useMutation({
    mutationFn: () =>
      api.put<ConfigSmtp>('/configuracoes/smtp', {
        host: form.host,
        porta: Number(form.porta),
        seguranca: form.seguranca,
        usuario: form.usuario.trim() || null,
        // Só manda a senha se o operador realmente digitou algo no campo.
        senha: senhaTocada ? form.senha : SENHA_INALTERADA,
        remetenteNome: form.remetenteNome,
        remetenteEmail: form.remetenteEmail,
        ativo: form.ativo,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['configuracoes', 'smtp'] }),
  });

  const testar = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; erro: string | null }>('/configuracoes/smtp/teste', { destino }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['configuracoes', 'smtp'] }),
  });

  if (isLoading) return <div className="h-64 animate-pulse rounded-card bg-borda-tenue" />;

  const salvo = data?.configurado ?? false;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
        <div className="space-y-5">
      <section className="rounded-card border border-borda bg-superficie p-6">
        <div className="mb-5 flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-chip bg-info-fundo text-info">
            <Mail size={19} />
          </span>
          <div>
            <h2 className="text-[15px] font-extrabold leading-tight">Servidor de e-mail</h2>
            <p className="mt-0.5 text-[12.5px] text-texto-suave">
              Usado para as notificações do painel. Enquanto estiver inativo, nada é enviado.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
            <Campo
              rotulo="Servidor SMTP"
              valor={form.host}
              aoMudar={(v) => setForm((f) => ({ ...f, host: v }))}
              placeholder="smtp.provedor.com.br"
              mono
            />
            <Campo
              rotulo="Porta"
              valor={String(form.porta)}
              aoMudar={(v) => setForm((f) => ({ ...f, porta: Number(v.replace(/\D/g, '')) || 0 }))}
              mono
            />
          </div>

          <label className="block">
            <Rotulo>Segurança</Rotulo>
            <select
              value={form.seguranca}
              onChange={(e) => {
                const seguranca = e.target.value as Seguranca;
                // Ajusta a porta junto: a combinação errada (465 com STARTTLS,
                // por exemplo) é o motivo mais comum de "conecta e trava".
                setForm((f) => ({ ...f, seguranca, porta: PORTA_PADRAO[seguranca] }));
              }}
              className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
            >
              <option value="STARTTLS">STARTTLS — porta 587 (recomendado)</option>
              <option value="TLS">TLS direto — porta 465</option>
              <option value="NENHUMA">Sem criptografia — porta 25</option>
            </select>
            {form.seguranca === 'NENHUMA' && (
              <p className="mt-1.5 text-[11.5px] leading-relaxed text-aviso">
                A senha trafega em claro. Só use para um relay na própria rede.
              </p>
            )}
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Usuário"
              valor={form.usuario}
              aoMudar={(v) => setForm((f) => ({ ...f, usuario: v }))}
              placeholder="deixe vazio se não exigir autenticação"
              mono
            />
            <label className="block">
              <Rotulo>{data?.temSenha && !senhaTocada ? 'Senha (já definida)' : 'Senha'}</Rotulo>
              <input
                type="password"
                value={form.senha}
                autoComplete="new-password"
                placeholder={data?.temSenha && !senhaTocada ? '•••••••• sem alteração' : ''}
                onChange={(e) => {
                  setSenhaTocada(true);
                  setForm((f) => ({ ...f, senha: e.target.value }));
                }}
                className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
              />
              {data?.temSenha && senhaTocada && form.senha === '' && (
                <p className="mt-1.5 text-[11.5px] text-aviso">
                  Salvar com o campo vazio remove a senha gravada.
                </p>
              )}
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Nome do remetente"
              valor={form.remetenteNome}
              aoMudar={(v) => setForm((f) => ({ ...f, remetenteNome: v }))}
            />
            <Campo
              rotulo="E-mail do remetente"
              valor={form.remetenteEmail}
              aoMudar={(v) => setForm((f) => ({ ...f, remetenteEmail: v }))}
              placeholder="rdns@consultnetworks.com.br"
              mono
            />
          </div>

          <label className="flex items-start gap-2.5 rounded-chip bg-fundo px-3 py-2.5">
            <input
              type="checkbox"
              checked={form.ativo}
              onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
              className="mt-0.5 size-4 accent-marca"
            />
            <span className="text-[12.5px] leading-snug">
              <span className="font-bold">Ativo</span> — o painel pode enviar e-mail.
              <span className="block text-texto-suave">
                Deixe desligado até o teste passar. É melhor não enviar do que enviar errado.
              </span>
            </span>
          </label>

          {salvar.error && (
            <p className="flex items-start gap-2 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
              <AlertTriangle size={15} className="mt-px shrink-0" />
              {(salvar.error as ApiError).descricao}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Botao
              variante="primario"
              onClick={() => salvar.mutate()}
              carregando={salvar.isPending}
              disabled={!form.host.trim() || !form.remetenteEmail.trim()}
            >
              Salvar
            </Botao>
            {salvar.isSuccess && !salvar.isPending && (
              <span className="text-[12.5px] font-semibold text-sucesso">Configuração salva.</span>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-card border border-borda bg-superficie p-6">
        <h2 className="text-[15px] font-extrabold leading-tight">Testar envio</h2>
        <p className="mt-0.5 text-[12.5px] text-texto-suave">
          Usa a configuração <strong>salva</strong>, não o que está no formulário — é ela que vai
          ser usada de verdade.
        </p>

        {!salvo ? (
          <p className="mt-4 rounded-chip bg-fundo px-3 py-2 text-[12.5px] text-texto-medio">
            Salve a configuração primeiro.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[240px] flex-1">
                <Campo
                  rotulo="Enviar para"
                  valor={destino}
                  aoMudar={setDestino}
                  placeholder="seu@email.com"
                  mono
                />
              </div>
              <Botao
                onClick={() => testar.mutate()}
                carregando={testar.isPending}
                disabled={!destino.includes('@')}
                iconeEsquerda={<Send size={14} />}
              >
                Enviar teste
              </Botao>
            </div>

            {testar.error && (
              <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
                {(testar.error as ApiError).descricao}
              </p>
            )}

            {data?.ultimoTesteEm && (
              <div
                className={`rounded-chip px-3 py-2.5 text-[12.5px] ${
                  data.ultimoTesteOk
                    ? 'bg-sucesso-fundo text-sucesso'
                    : 'bg-erro-fundo text-erro'
                }`}
              >
                <p className="flex items-center gap-2 font-semibold">
                  {data.ultimoTesteOk ? (
                    <CheckCircle2 size={15} />
                  ) : (
                    <AlertTriangle size={15} />
                  )}
                  Último teste{' '}
                  {data.ultimoTesteOk ? 'entregue' : 'falhou'} em{' '}
                  {new Date(data.ultimoTesteEm).toLocaleString('pt-BR', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </p>
                {data.ultimoTesteErro && (
                  // A mensagem crua do servidor é o conteúdo útil: "535
                  // authentication failed" e "certificate has expired" pedem
                  // correções diferentes.
                  <pre className="mt-1.5 overflow-x-auto whitespace-pre-wrap font-mono text-[11.5px] leading-relaxed opacity-90">
                    {data.ultimoTesteErro}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </section>
        </div>

        <div className="space-y-5">
          <SecaoBackupExterno />
        </div>
      </div>
    </div>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
      {children}
    </span>
  );
}

function Campo({
  rotulo,
  valor,
  aoMudar,
  placeholder,
  mono,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <label className="block">
      <Rotulo>{rotulo}</Rotulo>
      <input
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        placeholder={placeholder}
        className={`h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none ${
          mono ? 'font-mono' : ''
        }`}
      />
    </label>
  );
}
